/**
 * Sales landing page: the journey from a demo request to a paying account.
 *
 * It used to be the account funnel — free sign-ups on one end, paying
 * customers on the other — read entirely off the clients app. That is a real
 * picture, but it is the Clients module's picture, and it left the sales
 * module's landing page with no sales in it: not one deal, not one lead, not
 * one thing anybody on this team had done.
 *
 * What it shows now is the work: how many requests came in, how far they got,
 * where they came from, and which ones nobody has answered — ending at the
 * account on the ledger, which is where the Clients module picks the story up.
 *
 * ## One thing to a row
 *
 * Every block here is full width. Paired side by side, each of them had half
 * a screen for a list of six names and a bar chart of six sources, and the
 * two halves were read as one - "New 111 / 72%" beside "Ads 91 / 59%" invites
 * a comparison between two things that have nothing to do with each other.
 *
 * Every figure is recomputed from the live lists on each render. Nothing is
 * cached and nothing is seeded, so an empty workspace reads as empty.
 */
import { useApp } from '../store/AppContext.jsx';
import { parseDate, daysAgo } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState, Avatar } from '../components/Primitives.jsx';
import { TileRow } from '../components/StatCards.jsx';
import { SalesBriefCard } from '../components/AICards.jsx';
import { Funnel } from '../components/SalesCharts.jsx';
import PlanChip from '../components/PlanChip.jsx';
import {
  salesJourney, isTracked, isWorking, isStalled, companyName, leadDetails, clientOfDeal,
  originOf, ORIGINS, isDeal, leadDetails as detailsOf
} from '../domain/crm.js';

/** "Registration · Submitted 214" — a ranked list with a bar, and a click. */
function Ranked({ rows: list, total, onOpen, tone }) {
  if (!list.length) return <div className="cempty">Nothing yet.</div>;
  const top = list[0][1] || 1;
  return (
    <div className="ovrank">
      {list.map(([label, n]) => (
        <button key={label} type="button" className="ovrow" onClick={onOpen}>
          <span className="ovlab">{label}</span>
          <span className="ovbar"><i className={tone} style={{ width: `${Math.round(n / top * 100)}%` }} /></span>
          <b>{n}</b>
          <em>{Math.round(n / Math.max(1, total) * 100)}%</em>
        </button>
      ))}
    </div>
  );
}

/** Count by a key, biggest first, blanks dropped. */
const tally = (list, pick) => {
  const m = new Map();
  list.forEach(x => { const k = pick(x); if (k) m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
};

/*
 * Where a request came from, in its own colour.
 *
 * Six identical grey bars is a chart nobody reads down, and telling a lead
 * that cost money from one that did not is the whole use of this block.
 */
const SRC = [
  [/ad|ppc|paid|pmax/, 'ads', 'zap'],
  [/organic|search|seo/, 'organic', 'search'],
  [/refer/, 'referral', 'users'],
  [/direct/, 'direct', 'link'],
  [/social|insta|face|linked|tik/, 'social', 'star'],
  [/mail/, 'email', 'mail'],
  [/manual/, 'manual', 'edit']
];
const srcOf = s => SRC.find(([re]) => re.test(String(s || '').toLowerCase())) || [null, 'direct', 'zap'];

/** One source, as a share of every request. */
function SourceRow({ label, value, total, onOpen }) {
  const [, tone, icon] = srcOf(label);
  const pct = total ? Math.round((value / total) * 100) : 0;

  return (
    <button type="button" className={`srcrow s-${tone}`} onClick={onOpen}>
      <span className="srcic"><Icon name={icon} solid /></span>
      <span className="srcname">{label}</span>
      <span className="srcbar"><span style={{ width: `${Math.max(2, pct)}%` }} /></span>
      <span className="srcn">{value}</span>
      <span className="srcpct">{pct}%</span>
    </button>
  );
}

/** One request, as a row in either of the two lists at the foot of the page. */
function LeadRow({ d, crm, rows, right, onOpen }) {
  const f = leadDetails(crm, d);
  const client = clientOfDeal(rows, d);

  /*
   * A div that behaves like a button, not a <button>, because the row holds
   * the plan chip — and a chip that opens a list is itself a button. A button
   * inside a button is invalid HTML: React warns, and the browser is free to
   * close the outer one early and drop half the row out of it. The same shape
   * is used by the pipeline cards and the two list views in modals/.
   */
  return (
    <div
      className="leadrow"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <Avatar name={d.title} />
      <span className="leadwho">
        <b>{d.title}</b>
        <small>{f.email || companyName(crm, d.cid) || 'no address on the row'}</small>
        <span className="leadmeta">
          {companyName(crm, d.cid) && f.email ? (
            <span><Icon name="building" />{companyName(crm, d.cid)}</span>
          ) : null}
          {f.city ? <span><Icon name="pin" />{[f.city, f.country].filter(Boolean).join(', ')}</span> : null}
          {d.source ? <span><Icon name="zap" />{d.source}</span> : null}
        </span>
      </span>

      <span className="leadtags">
        {client ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
        <PlanChip list={f.productList} label={f.products} />
        {!d.owner ? <span className="dealchip">Unowned</span> : null}
      </span>

      <span className="leadstat">
        <b>{right(d, f).n}</b>
        <small>{right(d, f).l}</small>
      </span>
    </div>
  );
}

export default function SalesOverview() {
  const { S, crm, rows, setSheet } = useApp();

  /*
   * The whole book, not the tracker's share of it.
   *
   * This page counted `isTracked` alone and called every row a demo request.
   * Both were true when the sync only brought demo requests across; neither
   * is now. The board carries ten kinds of capture, a spreadsheet import and
   * rows somebody typed, and a page called Overview that silently left two
   * of the three out is a page that disagrees with the Pipeline beside it.
   *
   * `tracked` is kept for the blocks that are genuinely about the tracker —
   * a capture kind means nothing on a row that was typed in.
   */
  const everything = crm.deals;
  const tracked = crm.deals.filter(isTracked);
  const working = everything.filter(isWorking);
  const won = everything.filter(d => d.stage === 'Won');
  const lost = everything.filter(d => d.stage === 'Lost');
  const decided = won.length + lost.length;
  const winRate = decided ? Math.round((won.length / decided) * 100) : null;
  const stalled = working.filter(d => isStalled(S, crm, d)).length;
  const unowned = working.filter(d => !d.owner).length;

  const journey = salesJourney(S, crm, rows, { trackedOnly: true });
  const arrivedThisWeek = journey.weekly[journey.weekly.length - 1]?.leads || 0;

  /* The few worth acting on today, rather than a page of everything. */
  const freshLeads = [...working]
    .sort((a, b) => (parseDate(b.created) || 0) - (parseDate(a.created) || 0))
    .slice(0, 8);

  const oldest = [...working]
    .filter(d => isStalled(S, crm, d))
    .sort((a, b) => (parseDate(a.created) || 0) - (parseDate(b.created) || 0))
    .slice(0, 8);

  const bySource = tally(everything, d => d.source || 'Direct');

  const byStage = (S.stages || []).map(stage => ({
    stage, n: everything.filter(d => d.stage === stage).length
  })).filter(x => x.n);

  /*
   * The three dimensions the tracker actually gives us, and the one we add.
   *
   * Every figure below is counted off the rows on screen — nothing is cached,
   * nothing is seeded, and a workspace with no leads reads as having none.
   */
  const byOrigin = ORIGINS
    .map(o => [o, everything.filter(d => originOf(d) === o).length])
    .filter(([, n]) => n);

  const byKind = tally(tracked, d => d.leadType);

  const byState = tally(tracked, d => (
    d.leadState === 'submitted' ? 'Submitted'
      : d.leadState ? 'Partially submitted' : ''));

  const byCampaign = tally(everything, d => detailsOf(crm, d).campaign).slice(0, 8);

  /* Forms, not people: one visitor can fill in three. */
  const captures = tracked.reduce((t, d) => {
    const i = d.intel;
    return t + ((i && i.captures && i.captures.length) || (d.leadType ? 1 : 0));
  }, 0);

  const reachable = everything.filter(d => {
    const f = detailsOf(crm, d);
    return f.email || f.phone;
  }).length;

  const open = metric => setSheet({ kind: 'pipeStat', metric });

  return (
    <>
      <Card className="greet">
        <CardBody>
          <h3>{everything.length} lead{everything.length === 1 ? '' : 's'} and deal{everything.length === 1 ? '' : 's'} on the book</h3>
          <p>
            {working.length} in progress ·{' '}
            {won.length} won ·{' '}
            {lost.length} lost ·{' '}
            {arrivedThisWeek} arrived this week.
          </p>
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 5 }}>
              <span>{won.length} of {decided} decided were won</span>
              <span>{winRate === null ? 'No decisions yet' : `${winRate}% win rate`}</span>
            </div>
            <div className="track" style={{ background: 'rgba(255,255,255,.25)' }}>
              <div className="fill" style={{ width: `${winRate || 0}%`, background: '#fff' }} />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Each tile opens on the rows it counted: a figure with no way through
          to its list is a figure to worry about rather than act on. */}
      <TileRow
        tiles={[
          ['Everything', everything.length, 'users', 'g-purple', () => open('progress')],
          ['In progress', working.length, 'zap', 'g-green', () => open('progress')],
          ['This week', arrivedThisWeek, 'star', 'g-blue', () => open('week')],
          ['Unowned', unowned, 'alert', 'g-amber', () => open('unowned')],
          ['Won', won.length, 'check', 'g-cyan', () => open('won')],
          ['Stalled', stalled, 'clock', 'g-red', () => open('stalled')]
        ]}
      />

      <SalesBriefCard />

      <Card>
        <CardHead icon="flow" tone="g-green" title="Demo request to paying account" />
        <CardBody>
          <Funnel steps={journey.funnel} />
        </CardBody>
      </Card>

      {/*
        * Stages as steps rather than as bars.
        *
        * Two of them - New and Won - as a ranked bar chart said nothing a
        * sentence would not: the point of a stage list is the shape of the
        * board, which reads better as the stages in order with the count on
        * each than as lengths to compare.
        */}
      {/*
        * Where each row came from, which is the question this page could not
        * answer at all: leads, deals and an uploaded list live in one table,
        * and the Overview counted only the tracker's third of it.
        */}
      <Card>
        <CardHead icon="flow" tone="g-indigo" title="How they came in" count={everything.length} />
        <CardBody>
          <Ranked rows={byOrigin} total={everything.length} tone="t-indigo" onOpen={() => open('progress')} />
        </CardBody>
      </Card>

      {/* The tracker's own two dimensions. Both are counted over the tracked
          rows alone — a capture kind means nothing on a row somebody typed. */}
      {byKind.length ? (
        <Card>
          <CardHead icon="zap" tone="g-purple" title="What they filled in" count={tracked.length}>
            <span className="ovsub">{captures} capture{captures === 1 ? '' : 's'}</span>
          </CardHead>
          <CardBody>
            <Ranked rows={byKind} total={tracked.length} tone="t-purple" onOpen={() => open('progress')} />
          </CardBody>
        </Card>
      ) : null}

      {byState.length ? (
        <Card>
          <CardHead icon="check" tone="g-green" title="Did the form go through" count={tracked.length} />
          <CardBody>
            <Ranked rows={byState} total={tracked.length} tone="t-green" onOpen={() => open('progress')} />
            {/* Said once, here, because it is the figure people read wrong. */}
            <p className="ovnote">
              Partially submitted is not a failure — the tracker only marks a
              capture submitted when it recognises the thank-you page, and
              plenty of forms redirect or post by XHR instead.
            </p>
          </CardBody>
        </Card>
      ) : null}

      {byCampaign.length ? (
        <Card>
          <CardHead icon="mega" tone="g-pink" title="Campaigns that brought them" count={byCampaign.length} />
          <CardBody>
            <Ranked rows={byCampaign} total={everything.length} tone="t-pink" onOpen={() => open('progress')} />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHead icon="card" tone="g-blue" title="Everything by stage" count={everything.length} />
        <CardBody>
          {byStage.length ? (
            <div className="stagerail">
              {byStage.map(({ stage, n }, i) => (
                <div className="stagestep" key={stage}>
                  {i ? <span className="stagearrow"><Icon name="chev" /></span> : null}
                  <button
                    type="button"
                    className={`stagebox${stage === 'Won' ? ' won' : ''}`}
                    onClick={() => open(stage === 'Won' ? 'won' : 'progress')}
                  >
                    <b>{n}</b>
                    <small>{stage}</small>
                    <span className="stagepct">{Math.round((n / Math.max(1, everything.length)) * 100)}%</span>
                  </button>
                </div>
              ))}
            </div>
          ) : <div className="cempty">Nothing on the board yet.</div>}
        </CardBody>
      </Card>

      <Card>
        <CardHead icon="zap" tone="g-amber" title="Where they come from" count={everything.length} />
        <CardBody>
          {bySource.length ? (
            <div className="srclist">
              {bySource.map(([label, value]) => (
                <SourceRow
                  key={label}
                  label={label}
                  value={value}
                  total={everything.length}
                  onOpen={() => open('progress')}
                />
              ))}
            </div>
          ) : <div className="cempty">Nothing tracked yet.</div>}
        </CardBody>
      </Card>

      <Card>
        <CardHead icon="star" tone="g-orange" title="Newest leads" count={freshLeads.length}>
          <button type="button" className="btn sm" onClick={() => open('week')}>
            <Icon name="eye" />See them all
          </button>
        </CardHead>
        <div className="leadlist">
          {freshLeads.length ? freshLeads.map(d => (
            <LeadRow
              key={d.id}
              d={d}
              crm={crm}
              rows={rows}
              right={(x, f) => {
                const at = parseDate(x.created) || parseDate(f.requested);
                return { n: at ? `${daysAgo(at)}d` : '—', l: 'on the board' };
              }}
              onOpen={() => setSheet({ kind: 'dealView', id: d.id })}
            />
          )) : (
            <EmptyState icon="users" title="Nothing new">
              Nothing has come in from the tracker yet.
            </EmptyState>
          )}
        </div>
      </Card>

      <Card>
        <CardHead icon="clock" tone="g-red" title="Waiting longest" count={oldest.length}>
          <button type="button" className="btn sm" onClick={() => open('stalled')}>
            <Icon name="eye" />See them all
          </button>
        </CardHead>
        <div className="leadlist">
          {oldest.length ? oldest.map(d => (
            <LeadRow
              key={d.id}
              d={d}
              crm={crm}
              rows={rows}
              right={(x, f) => {
                const at = parseDate(f.requested) || parseDate(x.created);
                return { n: at ? `${daysAgo(at)}d` : '—', l: 'waiting' };
              }}
              onOpen={() => setSheet({ kind: 'dealView', id: d.id })}
            />
          )) : (
            <EmptyState icon="check" title="Nothing has gone quiet">
              Every open request has been touched recently.
            </EmptyState>
          )}
        </div>
      </Card>

    </>
  );
}
